/* =====================================================================
 * GlobalRuntimeHost — workspace-wide effects and global overlays.
 *
 * This host intentionally renders no page layout. It keeps process-level
 * runtime concerns alive while route elements choose their own page shell.
 * ===================================================================== */

import React, { Suspense, useEffect, useState } from 'react';
import {
  initializeWorkspaceEffects,
  onAppEvent,
  useUIStore,
  useUserStore,
} from '@auw/stores';
import {
  getSettingsModalComponent,
  useDMWebSocket,
} from '@auw/workspace-ui';
import { useFeature } from '@auw/feature-flags';

export const GlobalRuntimeHost: React.FC = () => {
  const user = useUserStore((s) => s.user);
  const hasUser = !!user;
  const settingsModalOpen = useUIStore((s) => s.settingsModalOpen);
  const settingsModalRequest = useUIStore((s) => s.settingsModalRequest);
  const setSettingsModalOpen = useUIStore((s) => s.setSettingsModalOpen);
  const openSettingsModal = useUIStore((s) => s.openSettingsModal);
  const messageEnabled = useFeature('mode.message');
  const [realtimeReady, setRealtimeReady] = useState(false);

  useEffect(() => {
    return initializeWorkspaceEffects({ messageEnabled });
  }, [messageEnabled]);

  useEffect(() => {
    if (!hasUser) {
      setRealtimeReady(false);
      return;
    }

    let cancelled = false;
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(() => {
        if (!cancelled) setRealtimeReady(true);
      }, { timeout: 1_500 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }

    const timer = window.setTimeout(() => {
      if (!cancelled) setRealtimeReady(true);
    }, 1_000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [hasUser]);

  useDMWebSocket(hasUser && realtimeReady && messageEnabled);

  useEffect(() => {
    return onAppEvent('app:openSettings', (detail) => openSettingsModal(detail));
  }, [openSettingsModal]);

  return (
    <Suspense fallback={null}>
      {(() => {
        const SettingsModalComp = getSettingsModalComponent();
        if (!SettingsModalComp) return null;
        return (
          <SettingsModalComp
            open={settingsModalOpen}
            initialTab={settingsModalRequest?.initialTab}
            focusSection={settingsModalRequest?.focusSection}
            requestId={settingsModalRequest?.requestId ?? 0}
            onClose={() => setSettingsModalOpen(false)}
          />
        );
      })()}
    </Suspense>
  );
};
